import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { schemas } from '@ciam/form-kit';
import { GigyaAPI } from '@ciam/gigya-api';
import { useLoginInfoStore } from '@features/shared';
import { useTranslation } from '@ciam/i18n';
import { tenureCheckApi } from '../apis';

type TenureCheckFormType = {
  code: string;
};

const DEFAULT_VALUES = {
  code: '',
};

const TENURE_CHECK_SCHEMA = z.object({
  code: schemas.code,
});

export const useTenureCheckForm = () => {
  const { t } = useTranslation(['tenure-check']);
  const { userInfo } = useLoginInfoStore();
  const methods = useForm<TenureCheckFormType>({
    mode: 'onChange',
    defaultValues: DEFAULT_VALUES,
    resolver: zodResolver(TENURE_CHECK_SCHEMA),
  });
  const { handleSubmit, setError } = methods;

  const handleSetRootError = (message: string) => {
    return setError('root', {
      type: 'manual',
      message: message,
    });
  };

  const updateTenure = async ({ uid }: { uid: string }) => {
    try {
      const res = await tenureCheckApi.updateTenure({ uid });
      console.log(res);
    } catch (error) {
      const err = error as Error;
      handleSetRootError(err.message);
    }
  };

  const onSubmit = handleSubmit(async (formData) => {
    console.log('formData', formData);
    try {
      const res = (await tenureCheckApi.verifyTenureCheckCode({ code: formData.code })) as unknown as {
        data: { errorCode: number; errorMessage: string };
      };

      if (res.data.errorCode === 0) {
        await updateTenure({ uid: userInfo.uid });
        return await GigyaAPI.socialize.notifyLogin({
          dontHandleScreenSet: true,
          siteUID: userInfo.uid,
          UIDSig: userInfo.uidSignature,
          UIDTimestamp: userInfo.signatureTimestamp,
        });
      }
      if (res.data.errorCode === 400006) {
        return handleSetRootError(t('errors.invalid-parameter-value'));
      }

      return handleSetRootError(res.data.errorMessage);
    } catch (error) {
      const err = error as { response: { data: string } };
      return handleSetRootError(`${t('errors.verify-default')} ${err.response.data}`);
    }
  });

  return { ...methods, handleSetRootError, onSubmit };
};
